'use client';

import { isEmpty, some, trim } from 'lodash-es';
import React from 'react';
import { IoIosPeople } from 'react-icons/io';
import { abbreviateNumber } from '../../utils/stringHelpers';
import FollowNonProfitButton from '../Buttons/FollowNonProfitButton';
import Avatar from '../next-ui/Avatar';
import Card from '../next-ui/Card';
import AppLink from '../next-ui/Link/Link';
import Typography from '../next-ui/Typography';
import Tooltip from '../next-ui/Tooltip';
import useStore from '@/stores/store';
import useJoinNonProfitDialogStore from '../store/joinNonProfitDialogSlice';
import {
  authorizedServerFetch,
  revalidatePage,
} from '@/actions/server.actions';
import useHasUserSession from '@/hooks/useHasUserSession';
import { BiWorld } from 'react-icons/bi';
import { MdVpnLock } from 'react-icons/md';
import { useRouter } from 'next/navigation';
import { getUserCountry } from '@/utils/user.helper';
import useNonProfitSignUpPopUpStore from '../store/nonProfitSignUpPopUpSlice';

function NonProfitCard(props) {
  const { nonProfit, title = '', className = '' } = props;
  const { avatar, name, city, state, country, numFollowers, company } =
    nonProfit;
  const { visibilityType, id } = company || {};

  const address = [city, state, country]
    .filter((a) => !isEmpty(trim(a)))
    .join(', ');
  let slug = nonProfit.slug ? `/${nonProfit.slug}` : undefined;
  if (!slug.includes('nonprofit')) {
    slug = `/nonprofit/${slug}`;
  }

  const router = useRouter();
  const hasUserSession = useHasUserSession();
  const setShowJoinNonProfitModal = useJoinNonProfitDialogStore(
    (state) => state.setOpen,
  );

  const setShowNonProfitSignUpPopUp = useNonProfitSignUpPopUpStore(
    (state) => state.setOpen,
  );

  const user = useStore((state) => state.user?.profile);
  const userCommunities = user?.communities;

  // const setShowJoinNonProfitModal = useJoinNonProfitDialogStore(
  //   (state) => state.setOpen,
  // );

  const isUserMemberOfNonProfit = some(userCommunities, {
    communityId: nonProfit.company_id,
  });

  const renderText = () => {
    let nonProfitCardText = null;
    if (isUserMemberOfNonProfit) {
      return 'Visit';
    }
    if (nonProfit?.isClaimed) {
      nonProfitCardText = 'Join';
    } else {
      nonProfitCardText = 'Visit';
    }
    return nonProfitCardText;
  };

  const renderNonProfitType = () => {
    if (nonProfit?.isClaimed) {
      if (nonProfit?.company?.visibilityType === 'private') {
        return (
          <>
            <MdVpnLock
              className="h-5 w-5 text-text-secondary"
              title="Users can join a gated community only after admin approval"
            />
            <Typography variant="body2" color="textSecondary">
              Gated
            </Typography>
          </>
        );
      } else if (nonProfit?.company?.visibilityType === 'public') {
        return (
          <>
            <BiWorld
              className="h-5 w-5 text-text-secondary"
              title="In an open community, users can join instantly—just sign up and become a member"
            />
            <Typography variant="body2" color="textSecondary">
              Open
            </Typography>
          </>
        );
      }
    } else {
      return (
        <Typography variant="body2" color="textSecondary">
          Not Claimed
        </Typography>
      );
    }
  };

  const handleCardClick = () => {
    if (hasUserSession) {
      if (isUserMemberOfNonProfit) {
        router.push(slug);
      } else if (!nonProfit?.isClaimed) {
        router.push(slug);
      } else if (nonProfit?.company?.visibilityType === 'public') {
        submitPublicNonProfitApplication();
      } else {
        setShowJoinNonProfitModal(true, nonProfit);
      }
    } else {
      if (nonProfit?.isClaimed) {
        // setShowJoinNonProfitModal(false, nonProfit);
        // const returnPath = `/onboard/nonprofit-user?nonProfitId=${nonProfit.id}`;
        // router.replace(
        //   `/api/auth/signup?returnTo=${encodeURIComponent(returnPath)}`,
        // );

        const loggedOutNonprofit = { ...nonProfit, logo_url: avatar };
        setShowNonProfitSignUpPopUp(true, loggedOutNonprofit);
      } else {
        router.push(slug);
      }
    }
  };

  const submitPublicNonProfitApplication = async () => {
    const res = await authorizedServerFetch(
      `users/me/nonprofit/${nonProfit.id}/apply`,
      {
        method: 'POST',
        body: JSON.stringify({
          message: '',
        }),
      },
    );

    if (res?.ok) {
      setShowJoinNonProfitModal(true, nonProfit, 'success');
    } else {
      if (res?.status === 409) {
        setShowJoinNonProfitModal(true, nonProfit, 'error');
      } else {
        setShowJoinNonProfitModal(true, nonProfit, 'backend-error');
      }
    }
  };

  return (
    <Card padding="none" edgeSize="lg" className={className}>
      <div className="flex flex-col gap-0.5 px-6 pb-3 pt-6 lg:px-8 lg:pt-8">
        <div className="flex flex-nowrap items-center justify-between gap-1">
          <AppLink href={slug} className="inline-grid w-full">
            <Typography
              variant="h3"
              component="h2"
              className="flex-1 truncate hover:text-text-hyperlink">
              {name}
            </Typography>
          </AppLink>
        </div>

        <Typography
          variant="body2"
          color="textSecondary"
          className="flex-1 truncate">
          {address}
        </Typography>

        <div className="flex w-full items-center justify-start gap-1">
          {renderNonProfitType()}
          <Typography variant="body2" color="textSecondary">
            {' | '}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {abbreviateNumber(numFollowers)} follower
            {nonProfit?.numFollowers > 1 ? 's' : ''}
          </Typography>

          {hasUserSession && (
            <div className="ml-auto flex items-center justify-center">
              <FollowNonProfitButton
                size="sm"
                nonProfit={nonProfit}
                noConfirmation
                revalidate={() => revalidatePage('/nonprofit')}
              />
            </div>
          )}
        </div>
      </div>

      <AppLink href={slug} className="flex w-full items-start justify-center">
        <Avatar
          src={avatar}
          alt={name}
          profile="npo"
          size="lg"
          shape="square"
          className="h-[151px] w-[151px] bg-transparent"
          role="link"
          data-href={slug}
        />
      </AppLink>

      <div className="h-0.5 w-full bg-body-background" />

      <div className="flex h-full min-h-[48px] w-full items-center justify-center py-1">
        {/* <AppLink href={slug}> */}
        <Typography
          component="span"
          variant="button"
          color="primary"
          className="hover:cursor-pointer"
          onClick={handleCardClick}>
          {renderText()}
        </Typography>
        {/* </AppLink> */}
      </div>
    </Card>
  );
}

export default NonProfitCard;
